import {
  Badge,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Field, useFormikContext } from 'formik';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { sxConfig } from './joker.style';
import { CheckboxUI, FormErrorUI, InputUI } from '../Interface';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const JokerFieldsTable = ({ setSelectListDialogData, handleCheckFieldDelete }) => {
  const { values, setFieldValue, errors } = useFormikContext();
  const theme = useTheme();
  const ui = theme.palette.ui;

  const getValueIndex = (values, id) => {
    return values.fields.findIndex((rc) => rc.id === id);
  };

  const maxVisOrder = Math.max(
    0,
    ...values.fields.filter((f) => f.groupped && f.visOrder != null).map((f) => f.visOrder)
  );

  const reindexVisOrders = (fields) => {
    let counter = 1;

    return fields.map((f) => (f.visOrder != null ? { ...f, visOrder: counter++ } : f));
  };

  const handleGrouppedChange = (checked, valueIndex) => {
    if (checked) {
      const hasName = values.fields[valueIndex].name;

      if (!hasName) {
        addNotification({
          content: `Csoportba sorolás előtt a megnevezés kitöltése kötelező!`,
          type: NotificationType.WARNING,
        });
        return;
      }
    }

    let updated = values.fields.map((f) => ({ ...f }));

    updated[valueIndex].groupped = checked;

    if (checked) {
      updated[valueIndex].visOrder = maxVisOrder + 1;
    } else {
      updated[valueIndex].visOrder = null;
      updated = reindexVisOrders(updated);
    }

    setFieldValue('fields', updated);
  };

  const reorderFields = (fields, changedIndex, newOrder) => {
    const current = fields[changedIndex];
    const oldOrder = current._prevOrder;

    if (!oldOrder || oldOrder === newOrder) return fields;

    return fields.map((f, i) => {
      if (!f.groupped) return f;

      if (i === changedIndex) {
        return { ...f, visOrder: newOrder };
      }

      if (oldOrder < newOrder) {
        if (f.visOrder > oldOrder && f.visOrder <= newOrder) {
          return { ...f, visOrder: f.visOrder - 1 };
        }
      }

      if (oldOrder > newOrder) {
        if (f.visOrder >= newOrder && f.visOrder < oldOrder) {
          return { ...f, visOrder: f.visOrder + 1 };
        }
      }

      return f;
    });
  };

  const handleOrderChange = (valueIndex, value) => {
    const newOrder = Number(value);
    const prevOrder = values.fields[valueIndex]._prevOrder;

    const max = Math.max(...values.fields.filter((f, i) => i !== valueIndex && f.groupped).map((f) => f.visOrder ?? 0));

    if (newOrder > max + 1 || newOrder < 1) {
      addNotification({
        content: `Érvénytelen sorrend!`,
        type: NotificationType.WARNING,
      });

      setFieldValue(`fields.${valueIndex}.visOrder`, prevOrder);
      return;
    }

    const updated = reorderFields(values.fields, valueIndex, newOrder);

    setFieldValue('fields', updated);
  };

  const handleDeleteField = (valueIndex) => {
    const field = values.fields[valueIndex];

    const updated = [...values.fields];

    updated[valueIndex] = {
      ...field,
      name: '',
      groupped: false,
      visOrder: null,
      jokerValues: [],
      isDeleted: !field.isNew,
    };

    setFieldValue('fields', reindexVisOrders(updated));
  };

  return (
    <Box sx={sxConfig.tableWrapper}>
      <TableContainer component={Paper} sx={sxConfig.tableContainer} className="custom-scrollbar">
        <Table sx={sxConfig.table}>
          <TableHead sx={{ background: ui.tableHeader }}>
            <TableRow>
              <TableCell width={50} />
              <TableCell width={120}>Mező</TableCell>
              <TableCell>Megnevezés</TableCell>
              <TableCell width={100}>Sorrend</TableCell>
              <TableCell width={110}>Választólista</TableCell>
              <TableCell width={70}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={sxConfig.tableBody}>
            {values.fields.map((item, index) => {
              const valueIndex = getValueIndex(values, item.id);
              return (
                <TableRow key={item.id} className={`${index % 2 === 0 ? 'bg-gray-100' : 'bg-white'}`}>
                  <TableCell>
                    {!item.isRow && (
                      <Field
                        onChange={(e) => handleGrouppedChange(e.target.checked, valueIndex)}
                        name={`fields.${valueIndex}.groupped`}
                        type="checkbox"
                        as={CheckboxUI}
                        sx={sxConfig.tableCheckbox}
                      />
                    )}
                  </TableCell>
                  <TableCell>{item.field}</TableCell>
                  <TableCell>
                    <Field as={InputUI} name={`fields.${valueIndex}.name`} size="small" placeholder="Megnevezés..." />
                  </TableCell>
                  <TableCell>
                    {!item.isRow && item.groupped && (
                      <Field
                        as={InputUI}
                        inputProps={{
                          min: 1,
                          max: maxVisOrder + 1,
                        }}
                        onFocus={(e) => {
                          setFieldValue(`fields.${valueIndex}._prevOrder`, values.fields[valueIndex].visOrder);
                        }}
                        type="number"
                        name={`fields.${valueIndex}.visOrder`}
                        size="small"
                        value={values.fields?.[valueIndex]?.visOrder || ''}
                        helperText={<FormErrorUI message={errors?.fields?.[valueIndex]?.visOrder} />}
                        onBlur={(e) => handleOrderChange(valueIndex, e.target.value)}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={sxConfig.tableCellCenter}>
                    {item.field.startsWith('joker_char') && !item.select && (
                      <Badge
                        badgeContent={item.jokerValues.length}
                        showZero
                        color="primary"
                        sx={sxConfig.badge}
                        onClick={() => {
                          const hasName = values.fields[valueIndex].name;

                          if (!hasName) {
                            addNotification({
                              content: `Választólista elemek felvétele előtt a megnevezés kitöltése kötelező!`,
                              type: NotificationType.WARNING,
                            });
                            return;
                          }

                          setSelectListDialogData({ open: true, valueIndex, originalValues: item.jokerValues });
                        }}
                      >
                        <EditIcon color="action" />
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell sx={sxConfig.tableCellCenter}>
                    <IconButton
                      onClick={() =>
                        handleCheckFieldDelete(valueIndex, () => {
                          handleDeleteField(valueIndex);
                        })
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default JokerFieldsTable;
